import React from "react";
import { Config, TemplateProps, Template, AnnouncementProps } from "src/types";
import { mockConfig } from "./constants/mockConfig";
import dynamic from "next/dynamic";

export type Page =
  | "home"
  | "announcement"
  | "announcementPage"
  | "branches"
  | "staff"
  | "about"
  | "listing"
  | "rent"
  | "rent-page"
  | "sale-page"
  | "shortlet-page"
  | "sale"
  | "shortlet"
  | "listingItem"
  | "contact";

// 🚀 OPTIMIZATION: Use dynamic imports instead of static imports
// This creates separate chunks and only loads what's needed
export const componentMap: {
  [key in Page]: {
    [key in Template]: () => Promise<{ default: React.FC<TemplateProps | AnnouncementProps> }>;
  };
} = {
  home: {
    design1: () => import("../components/templates/design1/Home").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("../components/templates/design2/Home").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/Home").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  announcement: {
    design1: () => import("../components/templates/design1/AnnouncementPage").then(m => ({ default: m.default as React.FC<AnnouncementProps> })),
    design2: () => import("../components/templates/design2/AnnouncementPage").then(m => ({ default: m.default as React.FC<AnnouncementProps> })),
    design3: () => import("./templates/design3/Pages/AnnouncementPage").then(m => ({ default: m.default as React.FC<AnnouncementProps> })),
  },
  announcementPage: {
    design1: () => import("../components/templates/design1/AnnouncementPostPage").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/AnnouncementPostPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/AnnouncementPostPage3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  branches: {
    design1: () => import("./templates/design1/Branches").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/Branches2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/Branches3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  staff: {
    design1: () => import("../components/templates/design1/Staff").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("../../app/components/templates/design2/StaffPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/StaffPage3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  about: {
    design1: () => import("../components/templates/design1/About1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/AboutUs2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/AboutUs3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  contact: {
    design1: () => import("./templates/design1/Contact1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/ContactUs2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/components/ContactUs3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  listing: {
    design1: () => import("./templates/design1/ListingPage1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/PropertyPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/PropertyPage").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  listingItem: {
    design1: () => import("../components/templates/design1/PropertyPage1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/PropertyPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design2/PropertyPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  rent: {
    design1: () => import("../components/templates/design1/Rent1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/RentPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/RentPage3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  sale: {
    design1: () => import("../components/templates/design1/Sale1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/SalePage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/SalePage3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  shortlet: {
    design1: () => import("../components/templates/design1/Shortlet1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/ShortletPage2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/ShortletPage").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  "shortlet-page": {
    design1: () => import("../components/templates/design1/ShortletPage1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/Shortlet2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/Shortlet3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  "sale-page": {
    design1: () => import("../components/templates/design1/SalePage1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/Sale2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/Sale3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
  "rent-page": {
    design1: () => import("../components/templates/design1/RentPage1").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design2: () => import("./templates/design2/Rent2").then(m => ({ default: m.default as React.FC<TemplateProps> })),
    design3: () => import("./templates/design3/Pages/Rent3").then(m => ({ default: m.default as React.FC<TemplateProps> })),
  },
};


const dynamicComponentCache: {
  [key: string]: React.ComponentType<TemplateProps | AnnouncementProps>;
} = {};

export function getDynamicComponent(
  page: Page,
  template: Template
): React.ComponentType<TemplateProps | AnnouncementProps> {
  const cacheKey = `${page}-${template}`;
  
  if (!dynamicComponentCache[cacheKey]) {
    dynamicComponentCache[cacheKey] = dynamic(
      componentMap[page][template],
      {
        loading: () => <div>Loading...</div>,
        ssr: true, // Enable SSR for better performance
      }
    ) as React.ComponentType<TemplateProps | AnnouncementProps>;
  }
  
  return dynamicComponentCache[cacheKey];
}


export const componentMapDynamic: {
  [key in Page]: {
    [key in Template]: React.ComponentType<TemplateProps | AnnouncementProps>;
  };
} = Object.entries(componentMap).reduce((acc, [page, templates]) => {
  acc[page as Page] = Object.entries(templates).reduce((templateAcc, [template, loader]) => {
    templateAcc[template as Template] = dynamic(loader, { ssr: true }) as React.ComponentType<
      TemplateProps | AnnouncementProps
    >;
    return templateAcc;
  }, {} as { [key in Template]: React.ComponentType<TemplateProps | AnnouncementProps> });

  return acc;
}, {} as { [key in Page]: { [key in Template]: React.ComponentType<TemplateProps | AnnouncementProps> } });

export async function selectTemplateComponent(
  page: Page,
  config?: Config,
  additionalProps: Partial<TemplateProps> = {},
  overrideTemplate?: Template // Optional override for demo mode
): Promise<React.JSX.Element> {
  const { styling } = mockConfig as Config;
  console.log({ overrideTemplate });
  
  const pageComponents = componentMap[page];
  const componentLoader = pageComponents["design1"] || pageComponents["design3"];
  

  const { default: SelectedComponent } = await componentLoader();
  
  return <SelectedComponent styling={styling} {...additionalProps} />;
}